import React, { useMemo, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getScoringConfig, previewScoringConfig, putScoringConfig, getAudit } from "@/lib/api";
import { useAppStore, SCENARIO_CAPTIONS } from "@/store/appStore";
import { useAuth } from "@/store/AuthContext";
import { SectionCard } from "@/components/tmo/primitives";
import { RingGauge } from "@/components/tmo/GaugeRadial";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  SlidersHorizontal, Lock, Save, RotateCcw, History, TrendingUp, TrendingDown, ShieldCheck, Info,
} from "lucide-react";

const WEIGHTS = [
  { key: "T1_test_recency", label: "Test Recency", hint: "Days since the most recent scenario test" },
  { key: "T4_test_depth", label: "Test Depth", hint: "AHOP exercised and load applied" },
  { key: "T2_scenario_coverage", label: "Scenario Coverage", hint: "Blue / Black / Beyond Black tested" },
  { key: "T3_test_outcome", label: "Test Outcome", hint: "Weighted pass rate over 180 days" },
];

function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function weightsSummary(w) {
  if (!w) return "—";
  return WEIGHTS.map((x) => `${x.label.split(" ")[1] || x.label} ${w[x.key] ?? "?"}`).join(" · ");
}

export default function Admin() {
  const scenario = useAppStore((s) => s.scenario);
  const { user } = useAuth();
  const qc = useQueryClient();
  const isDirector = user?.role === "DIRECTOR";

  const { data: config, isLoading } = useQuery({ queryKey: ["scoring-config"], queryFn: getScoringConfig });
  const { data: auditData } = useQuery({ queryKey: ["audit"], queryFn: getAudit });

  const [weights, setWeights] = useState(null);
  const [bands, setBands] = useState(null);

  useEffect(() => {
    if (config && !weights) {
      setWeights({ ...config.weights });
      setBands({ ...config.status_bands });
    }
  }, [config, weights]);

  const sum = weights ? Object.values(weights).reduce((a, b) => a + Number(b || 0), 0) : 0;
  const sumOk = Math.round(sum) === 100;
  const bandsOk = bands && Number(bands.READY) > Number(bands.AT_RISK) && Number(bands.AT_RISK) > 0 && Number(bands.READY) <= 100;

  const dirty = useMemo(() => {
    if (!config || !weights || !bands) return false;
    const wChanged = WEIGHTS.some((x) => Number(weights[x.key]) !== Number(config.weights[x.key]));
    const bChanged = Number(bands.READY) !== Number(config.status_bands.READY) || Number(bands.AT_RISK) !== Number(config.status_bands.AT_RISK);
    return wChanged || bChanged;
  }, [config, weights, bands]);

  const proposed = useMemo(() => {
    if (!config || !weights || !bands) return null;
    return { ...config, weights: { ...weights }, status_bands: { READY: Number(bands.READY), AT_RISK: Number(bands.AT_RISK) } };
  }, [config, weights, bands]);

  const { data: preview, isFetching: previewing } = useQuery({
    queryKey: ["scoring-preview", scenario, JSON.stringify(proposed?.weights), JSON.stringify(proposed?.status_bands)],
    queryFn: () => previewScoringConfig(proposed, scenario),
    enabled: !!proposed && sumOk && bandsOk,
    keepPreviousData: true,
  });

  const saveMut = useMutation({
    mutationFn: () => putScoringConfig(proposed, `${user?.name || "admin"} (${user?.role || "USER"})`),
    onSuccess: () => {
      toast.success("Scoring model updated", { description: "New weights are live across every screen." });
      qc.invalidateQueries();
    },
    onError: (e) => toast.error("Could not save", { description: e?.response?.data?.detail || "Please try again." }),
  });

  if (isLoading || !weights || !bands) {
    return <div className="space-y-4"><Skeleton className="h-24 rounded-lg" /><div className="grid grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-lg" />)}</div></div>;
  }

  const setW = (key, val) => setWeights((w) => ({ ...w, [key]: val }));
  const reset = () => { setWeights({ ...config.weights }); setBands({ ...config.status_bands }); };
  const events = (auditData?.events || []).filter((e) => e.action === "UPDATE_SCORING_CONFIG");
  const delta = preview ? Math.round((preview.proposed - preview.current) * 10) / 10 : null;

  return (
    <div className="space-y-4 pb-16" data-testid="admin-page">
      {/* Header + role banner */}
      <div className="rounded-xl border border-cnp-border bg-white p-5 flex items-center gap-3" data-testid="admin-intro">
        <span className="h-10 w-10 rounded-lg bg-cnp-blue/10 text-cnp-blue flex items-center justify-center"><SlidersHorizontal size={20} /></span>
        <div className="flex-1">
          <h2 className="text-lg font-extrabold text-cnp-navy">Readiness Models — Weight Editor</h2>
          <p className="text-sm text-cnp-textMuted">Tune the four Testing Readiness Index weights and the status bands. Changes recompute every score and are recorded in the audit trail.</p>
        </div>
        {!isDirector && (
          <span className="inline-flex items-center gap-2 rounded-lg bg-cnp-amber/15 text-[#8A5A00] px-3 py-2 text-xs font-semibold" data-testid="readonly-banner">
            <Lock size={14} /> Read-only — Director role required to edit
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Weights editor */}
        <SectionCard title="TRI Component Weights" subtitle="Must total 100%" className="lg:col-span-2">
          <div className="space-y-5">
            {WEIGHTS.map((wdef) => (
              <div key={wdef.key} data-testid={`weight-row-${wdef.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div>
                    <span className="text-sm font-semibold text-cnp-text">{wdef.label}</span>
                    <span className="text-[11px] text-cnp-textMuted ml-2">{wdef.hint}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={0} max={100}
                      data-testid={`weight-input-${wdef.key}`}
                      disabled={!isDirector}
                      value={weights[wdef.key]}
                      onChange={(e) => setW(wdef.key, Math.max(0, Math.min(100, Number(e.target.value))))}
                      className="w-16 text-right rounded-md border border-cnp-border px-2 py-1 text-sm tnum font-semibold text-cnp-navy focus:outline-none focus:ring-2 focus:ring-cnp-blue/40 disabled:bg-cnp-bg disabled:text-cnp-textMuted"
                    />
                    <span className="text-sm text-cnp-textMuted">%</span>
                  </div>
                </div>
                <Slider
                  data-testid={`weight-slider-${wdef.key}`}
                  value={[Number(weights[wdef.key])]}
                  min={0} max={100} step={1}
                  disabled={!isDirector}
                  onValueChange={(v) => setW(wdef.key, v[0])}
                />
              </div>
            ))}
          </div>

          <div className={`mt-5 flex items-center justify-between rounded-lg px-4 py-2.5 border ${sumOk ? "border-cnp-green/40 bg-cnp-green/5" : "border-cnp-red/40 bg-cnp-red/5"}`} data-testid="weight-sum">
            <span className="text-sm font-semibold text-cnp-text">Total weight</span>
            <span className={`tnum text-lg font-bold ${sumOk ? "text-cnp-green" : "text-cnp-red"}`}>{Math.round(sum)}%</span>
          </div>
          {!sumOk && <p className="text-xs text-cnp-red mt-1.5" data-testid="weight-sum-error">Weights must total exactly 100% before you can save (currently {Math.round(sum)}%).</p>}

          {/* Status bands */}
          <div className="mt-6 pt-5 border-t border-cnp-border">
            <div className="flex items-center gap-2 mb-3"><ShieldCheck size={15} className="text-cnp-blue" /><span className="text-[13px] uppercase tracking-wider font-semibold text-cnp-blue">Status Bands</span></div>
            <div className="grid grid-cols-2 gap-4">
              <div data-testid="band-ready">
                <label className="text-xs font-semibold text-cnp-text flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-cnp-green" /> Ready when score ≥</label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input type="number" min={0} max={100} disabled={!isDirector} data-testid="band-ready-input"
                    value={bands.READY} onChange={(e) => setBands((b) => ({ ...b, READY: Number(e.target.value) }))}
                    className="w-20 rounded-md border border-cnp-border px-2 py-1 text-sm tnum font-semibold focus:outline-none focus:ring-2 focus:ring-cnp-blue/40 disabled:bg-cnp-bg disabled:text-cnp-textMuted" />
                  <span className="text-sm text-cnp-textMuted">%</span>
                </div>
              </div>
              <div data-testid="band-at-risk">
                <label className="text-xs font-semibold text-cnp-text flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-cnp-amber" /> At Risk when score ≥</label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input type="number" min={0} max={100} disabled={!isDirector} data-testid="band-at-risk-input"
                    value={bands.AT_RISK} onChange={(e) => setBands((b) => ({ ...b, AT_RISK: Number(e.target.value) }))}
                    className="w-20 rounded-md border border-cnp-border px-2 py-1 text-sm tnum font-semibold focus:outline-none focus:ring-2 focus:ring-cnp-blue/40 disabled:bg-cnp-bg disabled:text-cnp-textMuted" />
                  <span className="text-sm text-cnp-textMuted">%</span>
                </div>
              </div>
            </div>
            <p className="text-[11px] text-cnp-textMuted mt-2">Below the At-Risk threshold, an application is <b>Not Ready</b>. The Ready threshold must be higher than the At-Risk threshold.</p>
            {!bandsOk && <p className="text-xs text-cnp-red mt-1" data-testid="bands-error">Ready must be greater than At Risk, and both between 1–100.</p>}
          </div>

          {isDirector && (
            <div className="mt-6 flex items-center gap-3">
              <button
                data-testid="save-config-button"
                disabled={!sumOk || !bandsOk || !dirty || saveMut.isPending}
                onClick={() => saveMut.mutate()}
                className="inline-flex items-center gap-2 rounded-lg bg-cnp-navy text-white px-4 py-2 text-sm font-semibold hover:bg-cnp-navyLight transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <Save size={15} /> {saveMut.isPending ? "Saving…" : "Save & Apply"}
              </button>
              <button data-testid="reset-config-button" disabled={!dirty} onClick={reset}
                className="inline-flex items-center gap-2 rounded-lg border border-cnp-border px-4 py-2 text-sm font-semibold text-cnp-text hover:bg-cnp-bg transition-colors disabled:opacity-40">
                <RotateCcw size={15} /> Reset
              </button>
            </div>
          )}
        </SectionCard>

        {/* Live preview */}
        <SectionCard title="Live Preview" subtitle={`Overall readiness · ${SCENARIO_CAPTIONS[scenario]}`}>
          {!sumOk || !bandsOk ? (
            <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="preview-blocked">
              <Info size={30} className="text-cnp-grey mb-3" />
              <p className="text-sm text-cnp-text font-medium">Preview paused</p>
              <p className="text-xs text-cnp-textMuted mt-1 max-w-[200px]">Fix the weight total and status bands to see the projected score.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center" data-testid="preview-panel">
              <RingGauge value={preview?.proposed ?? 0} status={(preview?.proposed ?? 0) >= bands.READY ? "READY" : (preview?.proposed ?? 0) >= bands.AT_RISK ? "AT_RISK" : "NOT_READY"} size={150} label="PROJECTED" />
              <div className="w-full mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-cnp-textMuted">Current (live)</span>
                  <span className="tnum font-bold text-cnp-navy" data-testid="preview-current">{preview?.current ?? "—"}%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-cnp-textMuted">Proposed</span>
                  <span className="tnum font-bold text-cnp-blue" data-testid="preview-proposed">{previewing ? "…" : `${preview?.proposed ?? "—"}%`}</span>
                </div>
                <div className="flex items-center justify-between text-sm border-t border-cnp-border pt-2">
                  <span className="text-cnp-textMuted">Change</span>
                  <span className="tnum font-bold inline-flex items-center gap-1" data-testid="preview-delta"
                    style={{ color: delta > 0 ? "#00A651" : delta < 0 ? "#D0021B" : "#5B6472" }}>
                    {delta > 0 ? <TrendingUp size={14} /> : delta < 0 ? <TrendingDown size={14} /> : null}
                    {delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta} pts`}
                  </span>
                </div>
              </div>
              {!dirty && <p className="text-[11px] text-cnp-textMuted mt-3 text-center">Editing weights updates the projection instantly.</p>}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Audit trail */}
      <SectionCard title="Audit Trail" subtitle="Every scoring-model change, most recent first" testid="audit-trail">
        <div className="flex items-center gap-2 mb-3 text-xs text-cnp-textMuted"><History size={14} /> {events.length} recorded change{events.length === 1 ? "" : "s"}</div>
        {events.length === 0 ? (
          <p className="text-sm text-cnp-textMuted py-6 text-center">No changes recorded yet. Saved edits will appear here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="audit-table">
              <thead><tr className="text-cnp-textMuted text-left border-b border-cnp-border">
                {["When", "Changed By", "Before", "After"].map((h) => <th key={h} className="pb-2 pr-3 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={i} className="border-b border-cnp-border/60 align-top" data-testid={`audit-row-${i}`}>
                    <td className="py-2 pr-3 tnum whitespace-nowrap text-cnp-text">{fmtTs(e.occurred_at)}</td>
                    <td className="py-2 pr-3 font-medium text-cnp-navy whitespace-nowrap">{e.actor}</td>
                    <td className="py-2 pr-3 text-cnp-textMuted">{weightsSummary(e.before?.weights)}</td>
                    <td className="py-2 pr-3 text-cnp-text">{weightsSummary(e.after?.weights)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
